<script setup lang="ts">
import { inject, onUnmounted, ref, watch } from 'vue'
import type { Message } from '@/types'
import SoundWaveIcon from '@/components/svg/SoundWaveIcon.vue'
import YellowRightArrow from '@/components/svg/YellowRightArrow.vue'
import { useChatsStore } from '@/store/chats'
import { homePageViewEnteredKey } from '@/constant/providerKey'
import { useCharAudioStore } from '@/store/charAudioStore'
import { isSingleChatKey, sekaiAudioPlayingKey } from '@/constant/symbol'

interface Props {
  message: Message
  chatId: string
  readonly?: boolean
  isShare?: boolean
}

const props = defineProps<Props>()

let charAudio: HTMLAudioElement | undefined
const chatStore = useChatsStore()
const charAudioStore = useCharAudioStore()
const canplay = ref(false)

watch(() => chatStore.currentChatId, (newChatId) => {
  if (newChatId !== props.chatId) {
    if (charAudio) {
      charAudio.pause()
      charAudio.currentTime = 0
    }
  }
})

// check if currently is the view of recommendation list home page
const isHomePageViewEntered = props.readonly ? null : inject(homePageViewEnteredKey)
const isSingleChat = props.readonly ? null : inject(isSingleChatKey)
const isAudioPlaying = props.readonly ? null : inject(sekaiAudioPlayingKey)

watch(() => isHomePageViewEntered?.value, (val) => {
  console.debug('[MessageRow] isHomePageViewEntered:', val)
  if (!val) {
    if (charAudio) {
      charAudio.pause()
    }
  }
  else {
    // Prevent playing after the list after insert new one
    if (charAudio && chatStore.currentChatId === props.chatId) {
      charAudio.play()
    }
  }
})

watch(() => props.message, (newMsg, oldMsg) => {
  const newUrl = newMsg.audioUrl
  const oldUrl = oldMsg.audioUrl
  if (!newUrl || newUrl === '') {
    resetAudio()
    return
  }
  if (newUrl && newUrl !== oldUrl) {
    handleAudioMessage()
  }
}, { deep: true })

function handleAudioMessage() {
  const messageAudioUrl = props.message.audioUrl
  console.log('handleAudioMessage for URL:', messageAudioUrl)
  // The condition for stop playing audio is
  // the currentChatId is not this chatId
  // isHomePageView is false and also is not isSingleChat
  if (chatStore.currentChatId !== props.chatId
  || (!(isHomePageViewEntered?.value || false) && !isSingleChat?.value)
  ) {
    console.log('handleAudioMessage: stop playing')
    return
  }
  if (charAudio && charAudio.src !== messageAudioUrl) {
    charAudio.pause()
    charAudio.currentTime = 0
  }

  if (!charAudio || charAudio.src !== messageAudioUrl) {
    canplay.value = false
    setPlayingToFalse()
    charAudio = new Audio(messageAudioUrl)
    charAudio.id = 'charAudio'
    // 若不可以自动播放，则静音
    // add listener for playing states
    charAudio.addEventListener('canplay', setCanPlay)
    charAudio.addEventListener('playing', setPlayingToTrue)
    charAudio.addEventListener('ended', setPlayingToFalse)
    charAudio.addEventListener('pause', setPlayingToFalse)
  }

  playAudio()
}

function playAudio() {
  if (!charAudio) {
    return
  }
  charAudio.play().then(() => {
    console.log('Audio playback started')
  }).catch((error: any) => {
    console.error('Error playing audio:', error)
    setPlayingToFalse()
  })
}

function setPlayingToTrue() {
  console.debug('[MessageRow] setPlayingToTrue')
  charAudioStore.setIsPlaying(true)
  if (isAudioPlaying?.value === false) {
    isAudioPlaying.value = true
  }
}

function setPlayingToFalse() {
  charAudioStore.setIsPlaying(false)
  if (isAudioPlaying?.value === true) {
    isAudioPlaying.value = false
  }
}

function setCanPlay() {
  console.debug('[MessageRow] setCanPlay')
  canplay.value = true
  setPlayingToTrue()
}

function resetAudio() {
  if (charAudio) {
    charAudio.pause()
    charAudio.currentTime = 0
    charAudio.removeEventListener('playing', setPlayingToTrue)
    charAudio.removeEventListener('ended', setPlayingToFalse)
    charAudio.removeEventListener('canplay', setCanPlay)
    charAudio.removeEventListener('pause', setPlayingToFalse)
  }
  setPlayingToFalse()
  canplay.value = false
}

onUnmounted(() => {
  resetAudio()
})
</script>

<template>
  <div
    :class="`w-full px-3 py-2 ${message.role === 'user' ? 'bg-user_message' : 'bg-char_message'} rounded-4 rounded-tl-2`"
    :style="{
      backdropFilter: 'blur(12px)',
      backgroundColor: isShare ? 'rgba(0, 0, 0, 0.7)' : undefined,
      padding: isShare ? '8px' : undefined,
    }"
  >
    <div class="flex">
      <p
        class="text-primary text-3 fw-600 leading-5" :style="{
          fontSize: isShare ? '14px' : undefined,
          whiteSpace: isShare ? 'nowrap' : undefined,
        }"
      >
        {{ message.name }}
      </p>
      <span
        v-if="canplay && props.message && props.message.audioUrl && !readonly"
        class="sound-wave-icon px-2 py-0.5 ml-2.5 rd-8"
        @touchend.stop="handleAudioMessage"
        @mouseup.stop="handleAudioMessage"
      >
        <sound-wave-icon />
      </span>
    </div>
    <div class="flex gap-2 items-end">
      <p
        class="text-on-bg-1 text-4 leading-5.5 font-helvetica-neue" :style="{
          lineHeight: isShare ? '1.2' : undefined,
          fontSize: isShare ? '14px' : undefined,
        }"
      >
        <span v-if="message.action" class="font-italic text-gray-300 mr-2">
          {{ `*${message.action}*` }}
        </span>
        <span class="fw-500">
          {{ message.message }}
        </span>
      </p>
      <div
        v-if="!readonly"
        class="w-6 h-6"
      >
        <yellow-right-arrow />
      </div>
    </div>
  </div>
</template>

<style>
.sound-wave-icon {
  box-shadow: 0px 6px 40px 0px rgba(25, 29, 29, 0.1),
    0px 2px 4px 0px rgba(25, 29, 29, 0.05);
  background: var(--white-trans-white-48, rgba(255, 255, 255, 0.7));
}
</style>
